import { useContext, useEffect, useRef } from "react";
import { HexStateContext } from "../State/Context";
import { imgFromCoords, videoFromCoords } from "../utils/settings";
import { videoDebugLog } from "../utils/videoDebugLog";

export type HexagenVideoProps = {
  x: number;
  y: number;
  onPlay?: () => void;
  onError?: () => void;
};

export default function HexagenVideo({ x, y, onPlay, onError }: HexagenVideoProps) {
  const { state } = useContext(HexStateContext);
  const videoRef = useRef<HTMLVideoElement>(null);
  const wasPlayingRef = useRef(false);
  const fallbackTimeoutRef = useRef<number | null>(null);
  const hasFallbackRef = useRef(false);

  const hasVideo =
    (state.hasVideo || []).some((h) => h.x === x && h.y === y) ||
    (state.selectedHexInfo?.x === x && state.selectedHexInfo?.y === y && state.selectedHexInfo?.videoStatus === "READY");

  useEffect(() => {
    const video = videoRef.current;
    if (!hasVideo || !video || !onError) return;

    const doFallback = (reason: string) => {
      // Never fallback on pause/suspend: Safari/iOS fires them during pan/scroll/tab switch.
      // Defensive: ignore even if some code path (e.g. cached bundle) still passes these.
      if (reason === "pause" || reason === "suspend") return;
      if (hasFallbackRef.current) return;
      hasFallbackRef.current = true;
      videoDebugLog("HexagenVideo FALLBACK", { x, y, reason });
      if (fallbackTimeoutRef.current) {
        clearTimeout(fallbackTimeoutRef.current);
        fallbackTimeoutRef.current = null;
      }
      video.style.display = "none";
      onError();
    };

    // Do not fallback on "pause" or "suspend": Safari/iOS fires them during pan/scroll/tab switch.
    // Fallback only on timeout (never played) or <video> onError.

    const FALLBACK_MS = 12000;
    fallbackTimeoutRef.current = window.setTimeout(() => {
      fallbackTimeoutRef.current = null;
      if (wasPlayingRef.current) return;
      if (video.readyState >= 2) return;
      doFallback("timeout");
    }, FALLBACK_MS);

    return () => {
      if (fallbackTimeoutRef.current) {
        clearTimeout(fallbackTimeoutRef.current);
        fallbackTimeoutRef.current = null;
      }
    };
  }, [hasVideo, x, y, onError]);

  useEffect(() => {
    videoDebugLog("HexagenVideo mount", { x, y, hasVideo });
    return () => {
      videoDebugLog("HexagenVideo unmount", { x, y });
    };
  }, []);

  if (!hasVideo) {
    videoDebugLog("HexagenVideo return null (no hasVideo)", { x, y });
    return null;
  }

  const isSelected = state.selectedHexInfo?.x === x && state.selectedHexInfo?.y === y;
  const cardVersion = isSelected ? state.selectedHexInfo?.cardVersion : undefined;
  const videoVersion = isSelected ? state.selectedHexInfo?.videoVersion : undefined;
  const videoSrc = videoFromCoords(x, y, videoVersion);
  console.log("[hex-video] HexagenVideo render", { x, y, hasVideo, videoSrc: videoSrc?.slice?.(0, 60) + "..." });

  return (
    <video
      ref={videoRef}
      muted
      playsInline
      autoPlay
      loop
      preload="auto"
      src={videoSrc}
      poster={imgFromCoords(x, y, cardVersion)}
      className="absolute inset-0 w-full h-full object-cover"
      onPlay={() => {
        wasPlayingRef.current = true;
        if (fallbackTimeoutRef.current) {
          clearTimeout(fallbackTimeoutRef.current);
          fallbackTimeoutRef.current = null;
        }
        console.log("[hex-video] HexagenVideo onPlay", { x, y });
        if (onPlay) onPlay();
      }}
      onPlaying={() => {
        wasPlayingRef.current = true;
        if (onPlay) onPlay();
      }}
      onCanPlay={() => console.log("[hex-video] HexagenVideo onCanPlay", { x, y })}
      onError={(e) => {
        const el = e.currentTarget as HTMLVideoElement;
        console.log("[hex-video] HexagenVideo onError", { x, y, error: el?.error?.message ?? el?.error?.code, src: videoSrc?.slice?.(0, 80) });
        videoDebugLog("HexagenVideo <video> onError", { x, y });
        el.style.display = "none";
        if (onError) onError();
      }}
      onStalled={() => console.log("[hex-video] HexagenVideo onStalled", { x, y })}
      onSuspend={() => console.log("[hex-video] HexagenVideo onSuspend", { x, y })}
    />
  );
}
